import React, { useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Scale,
    CoreScaleOptions,
    Tick,
    ChartOptions,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './styles.css';
import { Colors } from '../../utils/constants/colors';

// Register ChartJS components
ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

interface ChartData {
    name: string;
    attempted: number;    
    notAttempted: number; 
    totalMarks: number;   
    questions: {
        questionNumber: string;
        questionDescription: string;
        marksScored: number;
        maximumMarks: number;
    }[];
}

interface ComparisonChartProps {
    data: ChartData[];
}

const ComparisonChart: React.FC<ComparisonChartProps> = ({ data }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    const totalPages = Math.ceil(data.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = data.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const PaginationControls = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`pagination-button ${currentPage === i ? 'active' : ''}`}
                >
                    {i}
                </button>
            );
        }

        return (
            <div className="pagination-controls-comparison-table">
                <button
                    className="pagination-button nav"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    ←
                </button>
                {pages}
                <button
                    className="pagination-button nav"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    →
                </button>
            </div>
        );
    };

    const maxLimit = (): number => {
        const totalMarks = currentItems.map(row => row.totalMarks);
        const maxValue = Math.max(...totalMarks);
        return Math.round(maxValue) + 2; // Smaller padding since we're dealing with marks
    };

    const options: ChartOptions<'bar'> = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    font: {
                        size: window.innerWidth < 768 ? 12 : 16
                    },
                    boxWidth: window.innerWidth < 768 ? 12 : 16
                }
            },
            title: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    title: (context) => {
                        return context[0].label;
                    },
                    label: (context) => {
                        const datasetLabel = context.dataset.label || '';
                        const value = context.raw as number;
                        const topicData = currentItems[context.dataIndex];
                        const total = topicData.totalMarks;
                        
                        let label = [`${datasetLabel}: ${value}/${total} marks`];
                        
                        // Add question details based on dataset
                        if (context.datasetIndex === 0) { // Marks Scored
                            const scoredQuestions = topicData.questions.filter(q => q.marksScored > 0);
                            if (scoredQuestions.length > 0) {
                                label.push('\nScored in:');
                                scoredQuestions.forEach(q => {
                                    label.push(`${q.questionNumber}: ${q.marksScored}/${q.maximumMarks} - ${q.questionDescription}`);
                                });
                            }
                            const lostQuestions = topicData.questions.filter(q => q.marksScored < q.maximumMarks);
                            if (lostQuestions.length > 0) {
                                label.push('\nLost in:');
                                lostQuestions.forEach(q => {
                                    const lost = q.maximumMarks - q.marksScored;
                                    label.push(`${q.questionNumber}: ${lost}/${q.maximumMarks} - ${q.questionDescription}`);
                                });
                            }
                        }
                        
                        return label;
                    },
                },
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                padding: 12,
                bodySpacing: 4,
                bodyFont: {
                    size: 12
                },
                titleFont: {
                    size: 14,
                    weight: 'bold'
                }
            },
        },
        scales: {
            x: {
                min: 0,
                max: maxLimit(),
                title: {
                    display: true,
                    text: 'Marks',
                    font: {
                        size: window.innerWidth < 768 ? 12 : 16
                    }
                },
                grid: {
                    color: '#f0f0f0',
                },
                stacked: true,
                ticks: {
                    maxRotation: 0,
                    autoSkip: true,
                    font: {
                        size: window.innerWidth < 768 ? 10 : 14
                    }
                }
            },
            y: {
                grid: {
                    display: false,
                },
                stacked: true,
                ticks: {
                    autoSkip: true,
                    maxRotation: 0,
                    font: {
                        size: window.innerWidth < 768 ? 10 : 14
                    },
                    callback: function(this: Scale<CoreScaleOptions>, tickValue: string | number, index: number, ticks: Tick[]): string {
                        const label = this.getLabelForValue(Number(tickValue)) as string;
                        const maxLength = window.innerWidth < 768 ? 25 : 40;
                        return label.length > maxLength ? label.substr(0, maxLength) + '...' : label;
                    }
                },
                afterFit: (scale: any) => {
                    scale.width = window.innerWidth < 768 ? 200 : 300;
                }
            },
        },
        elements: {
            bar: {
                borderRadius: {
                    topLeft: 6,
                    topRight: 6,
                    bottomLeft: 6,
                    bottomRight: 6,
                    
               
                }
            }
        }
    };

    const chartData = {
        labels: currentItems.map(row => row.name),
        datasets: [
            {
                label: 'Marks Scored',
                data: currentItems.map(row => row.attempted),
                backgroundColor: Colors.success,
                borderRadius: 6,
            },
            {
                label: 'Marks Lost',
                data: currentItems.map(row => row.notAttempted),
                backgroundColor: 'rgba(211, 211, 211, 0.7)',
                borderRadius: 6,
            },
        ],
    };

    return (
        <div className="breakdown-chart-holder">
            <Bar options={options} data={chartData} />
            <PaginationControls />
        </div>
    );
};

export default ComparisonChart;